import { BottomSheet } from '@/components/base/BottomSheet';
import { usePwaInstall } from '@/hooks/usePwaInstall';

interface InstallPwaSheetProps {
  open: boolean;
  onClose: () => void;
}

export function InstallPwaSheet({ open, onClose }: InstallPwaSheetProps) {
  const { canInstall, isInstalled, isIos, isStandalone, install } = usePwaInstall();

  const handleInstall = async () => {
    const ok = await install();
    if (ok) {
      setTimeout(onClose, 500);
    }
  };

  if (isInstalled || isStandalone) {
    return (
      <BottomSheet open={open} onClose={onClose} title="Cài Agrida vào màn hình chính">
        <div className="py-6">
          <div className="flex items-center justify-center mb-4">
            <span className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center">
              <i className="ri-check-double-line text-4xl text-green-500" />
            </span>
          </div>
          <h3 className="text-base font-semibold text-gray-900 text-center mb-2">
            Đã cài đặt thành công!
          </h3>
          <p className="text-sm text-gray-500 text-center leading-relaxed">
            Agrida đã được cài vào màn hình chính. Bạn có thể mở nhanh ứng dụng bất cứ lúc nào.
          </p>
          <button
            type="button"
            onClick={onClose}
            className="mt-5 w-full py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap"
          >
            Đóng
          </button>
        </div>
      </BottomSheet>
    );
  }

  return (
    <BottomSheet open={open} onClose={onClose} title="Cài Agrida vào màn hình chính">
      <div className="py-4">
        <div className="flex items-center justify-center mb-4">
          <span className="w-20 h-20 rounded-full bg-agrida-50 flex items-center justify-center">
            <i className="ri-smartphone-line text-4xl text-agrida-500" />
          </span>
        </div>
        <p className="text-sm text-gray-600 text-center leading-relaxed mb-4">
          Cài Agrida vào màn hình chính để truy cập nhanh, không cần mở trình duyệt mỗi lần sử dụng.
        </p>

        {canInstall && (
          <div className="bg-agrida-50 rounded-xl p-4 mb-4">
            <p className="text-xs text-agrida-700 text-center mb-3 font-medium">
              Trình duyệt của bạn hỗ trợ cài đặt nhanh
            </p>
            <button
              type="button"
              onClick={handleInstall}
              className="w-full py-2.5 bg-agrida-600 text-white rounded-xl text-sm font-semibold hover:bg-agrida-700 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-download-2-line" />
              Cài ứng dụng
            </button>
          </div>
        )}

        {!canInstall && (
          <div className="space-y-3">
            {isIos ? (
              <div className="bg-amber-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-7 h-7 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                    <i className="ri-apple-fill text-amber-600 text-sm" />
                  </span>
                  <p className="text-sm font-semibold text-amber-800">iPhone / iPad (Safari)</p>
                </div>
                <ol className="text-xs text-amber-700 space-y-1.5 list-decimal list-inside leading-relaxed">
                  <li>Bấm nút <strong>Chia sẻ</strong> <i className="ri-share-forward-line" /> trên thanh công cụ Safari</li>
                  <li>Cuộn xuống và chọn <strong>Thêm vào Màn hình chính</strong> <i className="ri-add-box-line" /></li>
                  <li>Bấm <strong>Thêm</strong> để xác nhận</li>
                </ol>
              </div>
            ) : (
              <div className="bg-sky-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-7 h-7 rounded-xl bg-sky-100 flex items-center justify-center shrink-0">
                    <i className="ri-android-fill text-sky-600 text-sm" />
                  </span>
                  <p className="text-sm font-semibold text-sky-800">Android / Chrome</p>
                </div>
                <ol className="text-xs text-sky-700 space-y-1.5 list-decimal list-inside leading-relaxed">
                  <li>Bấm nút <strong>Menu</strong> <i className="ri-more-2-fill" /> ở góc trên phải Chrome</li>
                  <li>Chọn <strong>Thêm vào Màn hình chính</strong></li>
                  <li>Bấm <strong>Thêm</strong> để xác nhận</li>
                </ol>
              </div>
            )}

            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-7 h-7 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
                  <i className="ri-computer-line text-gray-500 text-sm" />
                </span>
                <p className="text-sm font-semibold text-gray-700">Máy tính (Desktop)</p>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                Trên máy tính, nhìn vào thanh địa chỉ trình duyệt, bấm biểu tượng <i className="ri-download-2-line" /> cài đặt ở góc phải.
              </p>
            </div>
          </div>
        )}

        <p className="text-xs text-gray-400 text-center mt-4">
          Cài đặt xong, Agrida sẽ xuất hiện như một app riêng trên thiết bị của bạn.
        </p>
      </div>
    </BottomSheet>
  );
}